/**
 * External Imports
 */
import { FC } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';
/**
 * Internal Imports
 */
import { RootState } from '../../redux/store';
import Events from '../../services/Analytics';

import { setLastPath } from '../../utils/helpers';
/**
 * Style Imports
 */
import { ReusableToolbar, Tab, TabGroup } from './ReusableTabs.styles';

interface TabProps {
  background?: string;
  coid: string;
  justify?: string;
  shadow?: boolean;
  tabItems: {
    base: string;
    links: { label: string; path: string }[];
  };
}
const ReusableTabs: FC<TabProps> = ({
  background,
  coid,
  justify = 'start',
  shadow = false,
  tabItems
}) => {
  const { pathname } = useLocation();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();
  return (
    <ReusableToolbar
      id="Reusable-Toolbar"
      background={background}
      justify={justify}
      shadow={shadow}
    >
      <TabGroup color="blue" unit="group">
        {tabItems.links.map(item => {
          return (
            <Link
              onClick={() => {
                const event = {
                  eventType: 'start',
                  org: {
                    orgId: coid,
                    orgLevel: 'Facility'
                  }
                };
                if (tabItems.base === '/employees') {
                  logTrackingEvent([
                    {
                      eventName: null,
                      module: Events.Employee.Module,
                      screen:
                        item.path === '/list/user'
                          ? Events.Employee.Screen.DirectReports.Name
                          : item.path === '/list/delegate'
                          ? Events.Employee.Screen.DirectReports.Name
                          : item.path === '/list/adhoc'
                          ? Events.Employee.Screen.Adhoc.Name
                          : item.path === '/issues'
                          ? Events.Employee.Screen.Issues.Name
                          : item.path === '/feedback'
                          ? Events.Employee.Screen.Feedback.Name
                          : item.path === '/stoplight'
                          ? Events.Employee.Screen.Stoplights.Name
                          : Events.Employee.Screen.Profile.Name,
                      ...event
                    }
                  ]);
                } else if (tabItems.base === '/patients') {
                  logTrackingEvent([
                    {
                      eventName: null,
                      module: Events.Patient.Module,
                      screen:
                        item.path === '/census'
                          ? Events.Patient.Screen.Census.Name
                          : item.path === '/issues'
                          ? Events.Patient.Screen.Issues.Name
                          : item.path === '/feedback'
                          ? Events.Patient.Screen.Feedback.Name
                          : item.path === '/stoplight'
                          ? Events.Patient.Screen.Stoplights.Name
                          : Events.Patient.Screen.Profile.Name,
                      ...event
                    }
                  ]);
                } else if (tabItems.base === '/support') {
                  if (item.path.includes('cned')) {
                    logTrackingEvent([
                      {
                        eventName: null,
                        module: Events.CNEd.Module,
                        screen:
                          item.path === '/cned/list'
                            ? Events.CNEd.Screen.List.Name
                            : item.path === '/cned/issues'
                            ? Events.CNEd.Screen.Issues.Name
                            : item.path === '/cned/feedback'
                            ? Events.CNEd.Screen.Feedback.Name
                            : Events.CNEd.Screen.Profile.Name,
                        ...event
                      }
                    ]);
                  } else {
                    logTrackingEvent([
                      {
                        eventName: null,
                        module: Events.CSRN.Module,
                        screen:
                          item.path === '/csc/list'
                            ? Events.CSRN.Screen.List.Name
                            : item.path === '/csc/issues'
                            ? Events.CSRN.Screen.Issues.Name
                            : item.path === '/csc/feedback'
                            ? Events.CSRN.Screen.Feedback.Name
                            : Events.CSRN.Screen.Profile.Name,
                        ...event
                      }
                    ]);
                  }
                } else if (tabItems.base === '/validation') {
                  logTrackingEvent([
                    {
                      eventName: null,
                      module: Events.Validation.Module,
                      screen:
                        item.path === '/validation/list'
                          ? Events.Validation.Screen.List.Name
                          : Events.Validation.Screen.Profile.Name,
                      ...event
                    }
                  ]);
                } else if (tabItems.base === '/reports/patient') {
                  logTrackingEvent([
                    {
                      eventName: null,
                      module: Events.Report.Module,
                      screen:
                        item.path === '/compliance'
                          ? Events.Report.Screen.PatientCompliance.Name
                          : item.path === '/rounds'
                          ? Events.Report.Screen.PatientRounds.Name
                          : Events.Report.Screen.PatientCensus.Name,
                      ...event
                    }
                  ]);
                } else if (tabItems.base === '/reports/employee') {
                  logTrackingEvent([
                    {
                      eventName: null,
                      module: Events.Report.Module,
                      screen:
                        item.path === '/rounds'
                          ? Events.Report.Screen.EmployeeRounds.Name
                          : Events.Report.Screen.EmployeeFreq.Name,
                      ...event
                    }
                  ]);
                }
                setLastPath(tabItems.base + item.path);
              }}
              key={item.path}
              to={tabItems.base + item.path}
            >
              <Tab
                id={`Reusable-Tab-${item.label}`}
                data-testid={`reusable-${view}-tab-${item.label.toLowerCase()}`}
                className="neu-text-gray-60"
                active={
                  (view === 'employee' &&
                    item.path.split('/').pop() === 'rounds') ||
                  (view === 'patient' &&
                    item.path.split('/').pop() === 'compliance') ||
                  pathname.includes(item.path)
                }
                unit="tab"
              >
                {item.label}
              </Tab>
            </Link>
          );
        })}
      </TabGroup>
    </ReusableToolbar>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  coid: state.AuthorizedUser?.authorizedUser.coid
});

export default connect(mapReduxStateToProps)(ReusableTabs);
